import classNames from 'classnames';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';


import { Stack } from 'ui/stack';
import { Box } from 'ui/box';
import { mergeSx } from 'ui/merge-sx';

import Badge from 'components/partials/badge/badge';

import Icons from 'assets/icons';

import ExtensionUpsellTile from 'containers/user/critical-elements-monitoring/components/extension-upsell-tile';
import { getPrimaryMetric } from 'containers/user/critical-elements-monitoring/helpers/get-primary-metric';

import {
  BAR_PAIR_LABELS,
  COMPARATOR_ORDER,
  getComparatorLabel,
  getComparatorFieldForMode,
  getComparatorUnit,
  formatProgressToFinal,
  ZERO_BASE_COMPARATOR_TEXT,
} from 'containers/user/critical-elements-monitoring/monitoring-constants';
import {
  formatMetricValue,
  formatMonitoringPercent,
  formatSignedMonitoringValue,
  PERCENT_DECIMALS,
} from 'containers/user/critical-elements-monitoring/helpers/format-monitoring-value';
import type { ComparatorKey } from 'containers/user/critical-elements-monitoring/monitoring-constants';
import type {
  CompareTo,
  FunnelStageMetric,
  MetricValue,
  PercentMetricValue,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

export type FunnelDisplayMode = 'volume' | 'rate';

export type StageCardDisplayMode = FunnelDisplayMode | 'proportion';

export type StageCardMetric = Pick<
  FunnelStageMetric,
  'volume' | 'rate' | 'proportion' | 'comparators'
>;

export type StageCardDataStatus =
  | 'not-recorded'
  | 'data-error'
  | 'metric-not-available'
  | 'prospect-upsell';

type MessageDataStatus = Exclude<StageCardDataStatus, 'prospect-upsell'>;

const PROSPECT_UPSELL_CONTACT_TEST_ID = 'inquiry-rate-upsell-contact';

export interface FunnelStageCardProps {
  stageLabel: string;
  metric: StageCardMetric;
  compareTo: CompareTo;
  displayMode: StageCardDisplayMode;
  cohortLabel: string;
  secondaryCaption?: string;
  lastUpdatedLabel?: string;
  hideBarsSection?: boolean;
  statusBadge?: { text: string; variant: 'warning' };
  dataStatus?: StageCardDataStatus;
  cohortDataUnavailable?: boolean;
  headerAction?: ReactNode;
}

interface DeltaBadge {
  text: string;
  variant: 'success-filled' | 'critical-filled' | 'neutral-filled';
  icon?: 'up' | 'down';
}

const renderDeltaIcon = (icon: DeltaBadge['icon']) => {
  if (icon === 'up') {
    return <Icons.ArrowUpRight className="size-3.5" aria-hidden="true" />;
  }

  if (icon === 'down') {
    return <Icons.ArrowDownRight className="size-3.5" aria-hidden="true" />;
  }

  return undefined;
};

const ZERO_CHANGE_BADGE_TEXT = '0 pp';

const isPercentMetric = (
  metric: MetricValue | PercentMetricValue,
  displayMode: StageCardDisplayMode
): metric is PercentMetricValue => displayMode !== 'volume';

const getDeltaValue = (
  metric: MetricValue | PercentMetricValue,
  displayMode: StageCardDisplayMode
): number | null => {
  if (!isPercentMetric(metric, displayMode)) return metric.deltaPct;

  return metric.pp;
};

const getDeltaBadge = (
  metric: MetricValue | PercentMetricValue,
  displayMode: StageCardDisplayMode
): DeltaBadge | null => {
  const delta = getDeltaValue(metric, displayMode);

  if (delta === null) return null;

  const unit = displayMode === 'volume' ? '%' : ' pp';
  const signedDelta =
    displayMode === 'volume' ? delta : Number(delta.toFixed(PERCENT_DECIMALS));

  if (displayMode !== 'volume' && signedDelta === 0) {
    return { text: ZERO_CHANGE_BADGE_TEXT, variant: 'neutral-filled' };
  }

  const isPositive = signedDelta >= 0;
  const sign = signedDelta > 0 ? '+' : '';

  return {
    text:
      displayMode === 'volume'
        ? `${sign}${signedDelta}${unit}`
        : formatSignedMonitoringValue(delta, unit),
    variant: isPositive ? 'success-filled' : 'critical-filled',
    icon: isPositive ? 'up' : 'down',
  };
};

const formatComparatorValue = (
  value: number,
  key: ComparatorKey,
  displayMode: StageCardDisplayMode
): string =>
  displayMode === 'volume'
    ? `${value > 0 ? '+' : ''}${value}${getComparatorUnit(key, displayMode)}`
    : formatSignedMonitoringValue(value, getComparatorUnit(key, displayMode));

const getComparatorDisplayValue = (
  key: ComparatorKey,
  value: number,
  displayMode: StageCardDisplayMode,
  isProgressRow: boolean,
  isZeroBaseComparator: boolean
): string => {
  if (isZeroBaseComparator) return ZERO_BASE_COMPARATOR_TEXT;
  if (isProgressRow) return formatProgressToFinal(value);

  return formatComparatorValue(value, key, displayMode);
};

const getComparatorChipValue = (
  key: ComparatorKey,
  metric: StageCardMetric,
  displayMode: StageCardDisplayMode,
  compareTo: CompareTo
): number | null | undefined => {
  if (displayMode === 'volume' || key === 'vsCohort') return undefined;
  if (compareTo === 'prior-year') return undefined;

  if (key === 'client') {
    return displayMode === 'rate'
      ? metric.rate.current
      : metric.proportion.current;
  }

  return displayMode === 'rate'
    ? metric.comparators.cohortRateCurrent
    : metric.comparators.cohortProportionCurrent;
};

interface DataStatusConfig {
  tileClasses: string;
  headerBorderSx: SxProps<Theme>;
  messageSx: SxProps<Theme>;
  message: string;
}

const COMPARISON_NOT_AVAILABLE_MESSAGE =
  "Comparison isn't available for the selected criteria.";

const DATA_STATUS_CONFIG: Record<MessageDataStatus, DataStatusConfig> = {
  'not-recorded': {
    tileClasses: 'bg-gray-50 border border-dashed border-gray-300 rounded-lg',
    headerBorderSx: {
      borderBottomWidth: '1px',
      borderColor: 'tokens.gray.200',
    },
    messageSx: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      color: 'tokens.gray.700',
    },
    message: 'Data is not available for the selected criteria.',
  },
  'data-error': {
    tileClasses: 'bg-amber-50 border border-dashed border-amber-300 rounded-lg',
    headerBorderSx: {
      borderBottomWidth: '1px',
      borderColor: 'tokens.amber.200',
    },
    messageSx: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      color: 'tokens.amber.700',
    },
    message: 'Unable to calculate benchmarks due to missing data.',
  },
  'metric-not-available': {
    tileClasses: 'bg-gray-50 border border-dashed border-gray-300 rounded-lg',
    headerBorderSx: {
      borderBottomWidth: '1px',
      borderColor: 'tokens.gray.200',
    },
    messageSx: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      color: 'tokens.gray.700',
    },
    message: "Metric isn't available for the selected criteria.",
  },
};

const FunnelStageCard = ({
  stageLabel,
  metric,
  compareTo,
  displayMode,
  cohortLabel,
  secondaryCaption,
  lastUpdatedLabel,
  hideBarsSection = false,
  statusBadge,
  dataStatus,
  cohortDataUnavailable = false,
  headerAction,
}: FunnelStageCardProps) => {
  if (dataStatus === 'prospect-upsell') {
    return (
      <ExtensionUpsellTile
        title={stageLabel}
        type="prospect"
        contactTestId={PROSPECT_UPSELL_CONTACT_TEST_ID}
      />
    );
  }

  if (dataStatus) {
    const dataStatusConfig = DATA_STATUS_CONFIG[dataStatus];

    return (
      <div
        className={classNames(
          'flex w-full flex-col',
          dataStatusConfig.tileClasses
        )}
      >
        <Stack
          sx={mergeSx(
            {
              alignItems: 'flex-start',
              gap: 0.25,
              px: 1.5,
              pt: 1,
              pb: '9px',
            },
            dataStatusConfig.headerBorderSx
          )}
        >
          <h3 className="text-sm font-semibold text-gray-700">{stageLabel}</h3>
          {lastUpdatedLabel && (
            <p className="text-xs text-gray-500">
              Last updated on {lastUpdatedLabel}
            </p>
          )}
        </Stack>

        <Box sx={{ px: 1.5, py: 1.25 }}>
          <Box component="p" sx={dataStatusConfig.messageSx}>
            {dataStatusConfig.message}
          </Box>
        </Box>
      </div>
    );
  }

  const primaryMetric = getPrimaryMetric(metric, displayMode);
  const isPriorYear = compareTo === 'prior-year';
  const isProgressToFinal = isPriorYear && displayMode === 'volume';
  const hideYoYComparators = isPriorYear && !isProgressToFinal;
  const isZeroBaseVolume =
    displayMode === 'volume' && primaryMetric.previous === 0;
  const deltaBadge =
    isPriorYear || isZeroBaseVolume
      ? null
      : getDeltaBadge(primaryMetric, displayMode);
  const showPriorCurrentBars =
    !hideBarsSection &&
    primaryMetric.previous !== null &&
    primaryMetric.current !== null;
  const barPairLabels = BAR_PAIR_LABELS[compareTo];

  const maxValue = Math.max(
    primaryMetric.current ?? 0,
    primaryMetric.previous ?? 0,
    1
  );

  const comparatorEntries = COMPARATOR_ORDER.map((key) => ({
    key,
    value: metric.comparators[getComparatorFieldForMode(key, displayMode)],
  })).filter(
    (entry): entry is { key: ComparatorKey; value: number } =>
      entry.value !== undefined
  );

  const renderableComparatorEntries = comparatorEntries.filter((entry) => {
    if (hideYoYComparators && entry.key !== 'vsCohort') return false;
    if (cohortDataUnavailable && entry.key !== 'client') return false;

    return true;
  });

  return (
    <div className="group flex w-full flex-col rounded-lg border border-navy-100 bg-white">
      <Stack
        sx={{
          position: 'relative',
          alignItems: 'flex-start',
          gap: 1,
          borderBottomWidth: '1px',
          borderColor: 'tokens.navy.100',
          px: 1.5,
          pt: 1,
          pb: '9px',
        }}
      >
        <Box
          component="h3"
          sx={[
            {
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              fontWeight: 600,
              color: 'tokens.gray.700',
            },
            !!headerAction && { pr: 4 },
          ]}
        >
          {stageLabel}
        </Box>
        {lastUpdatedLabel && (
          <p className="text-xs text-gray-500">
            Last updated on {lastUpdatedLabel}
          </p>
        )}
        {headerAction && (
          <Box sx={{ position: 'absolute', top: '0.5rem', right: '0.75rem' }}>
            {headerAction}
          </Box>
        )}
      </Stack>

      <Box sx={{ px: 1.5, py: 1.25 }}>
        {primaryMetric.current === null ? (
          <Box
            sx={{
              borderRadius: '0.25rem',
              bgcolor: 'tokens.gray.50',
              borderWidth: '1px',
              borderStyle: 'dashed',
              borderColor: 'tokens.gray.300',
              p: '9px',
            }}
          >
            <span className="text-xs text-gray-700">
              {COMPARISON_NOT_AVAILABLE_MESSAGE}
            </span>
          </Box>
        ) : (
          <>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
              <span className="text-lg font-bold text-gray-700">
                {formatMetricValue(primaryMetric.current, displayMode)}
              </span>
              {deltaBadge && (
                <Badge
                  text={deltaBadge.text}
                  variant={deltaBadge.variant}
                  icon={renderDeltaIcon(deltaBadge.icon)}
                />
              )}
              {statusBadge && (
                <Badge text={statusBadge.text} variant="warning-filled" />
              )}
            </Stack>
            {secondaryCaption && (
              <p className="mt-1 text-xs text-gray-500">{secondaryCaption}</p>
            )}
          </>
        )}
      </Box>

      {showPriorCurrentBars &&
        primaryMetric.previous !== null &&
        barPairLabels && (
          <Stack sx={{ gap: 2, px: 1.5, py: 1 }}>
            <Stack sx={{ gap: 1 }}>
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '0.75rem',
                  lineHeight: '1rem',
                  color: 'tokens.gray.700',
                }}
              >
                <span>{barPairLabels.prior}</span>
                <span>
                  {formatMetricValue(primaryMetric.previous, displayMode)}
                </span>
              </Stack>
              <Box
                sx={{
                  height: '0.625rem',
                  borderRadius: '9999px',
                  bgcolor: 'tokens.gray.100',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    borderRadius: '9999px',
                    bgcolor: 'tokens.gray.300',
                  }}
                  style={{
                    width: `${Math.min(
                      100,
                      (Math.max(primaryMetric.previous ?? 0, 0) / maxValue) *
                        100
                    )}%`,
                  }}
                />
              </Box>
            </Stack>

            <Stack sx={{ gap: 1 }}>
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '0.75rem',
                  lineHeight: '1rem',
                  color: 'tokens.gray.500',
                }}
              >
                <span>{barPairLabels.current}</span>
                <b className="text-blue-700 font-semibold">
                  {formatMetricValue(primaryMetric.current ?? 0, displayMode)}
                </b>
              </Stack>
              <Box
                sx={{
                  height: '0.625rem',
                  borderRadius: '9999px',
                  bgcolor: 'tokens.gray.100',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    borderRadius: '9999px',
                    bgcolor: 'tokens.blue.500',
                  }}
                  style={{
                    width: `${Math.min(
                      100,
                      (Math.max(primaryMetric.current ?? 0, 0) / maxValue) * 100
                    )}%`,
                  }}
                />
              </Box>
            </Stack>
          </Stack>
        )}

      {(!!renderableComparatorEntries.length || cohortDataUnavailable) && (
        <Stack sx={{ flex: '1 1 0%', gap: 0.5, px: 1.5, py: 1 }}>
          {renderableComparatorEntries.map(({ key, value }) => {
            const isProgressRow = isProgressToFinal && key !== 'vsCohort';
            const isZeroBaseComparator =
              isZeroBaseVolume && (key === 'client' || key === 'vsCohort');
            const signedValue =
              displayMode === 'volume'
                ? value
                : Number(value.toFixed(PERCENT_DECIMALS));
            const isPositiveComparator =
              displayMode === 'volume' ? signedValue >= 0 : signedValue > 0;
            const isNegativeComparator = signedValue < 0;
            const chipValue = getComparatorChipValue(
              key,
              metric,
              displayMode,
              compareTo
            );

            return (
              <Stack
                key={key}
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: '0.25rem',
                  bgcolor: 'tokens.gray.50',
                  borderWidth: '1px',
                  borderColor: 'tokens.gray.100',
                  px: 1,
                  py: '3px',
                  fontSize: '0.75rem',
                  lineHeight: '1rem',
                }}
              >
                <span className="text-gray-700">
                  {getComparatorLabel(key, cohortLabel, isProgressRow)}
                </span>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
                  {chipValue !== null && chipValue !== undefined && (
                    <span className="rounded bg-gray-200 px-1 text-xs font-semibold text-gray-500">
                      {formatMonitoringPercent(chipValue)}
                    </span>
                  )}
                  <span
                    className={classNames('font-semibold', {
                      'text-gray-500': isProgressRow || isZeroBaseComparator,
                      'text-success-700':
                        !isProgressRow &&
                        !isZeroBaseComparator &&
                        isPositiveComparator,
                      'text-critical-700':
                        !isProgressRow &&
                        !isZeroBaseComparator &&
                        isNegativeComparator,
                    })}
                  >
                    {getComparatorDisplayValue(
                      key,
                      value,
                      displayMode,
                      isProgressRow,
                      isZeroBaseComparator
                    )}
                  </span>
                </Stack>
              </Stack>
            );
          })}
          {cohortDataUnavailable && (
            <Box
              sx={{
                borderRadius: '0.25rem',
                bgcolor: 'tokens.gray.50',
                borderWidth: '1px',
                borderStyle: 'dashed',
                borderColor: 'tokens.gray.300',
                p: '9px',
              }}
            >
              <span className="text-xs text-gray-700">
                Cohort metrics are not available for the selected criteria.
              </span>
            </Box>
          )}
        </Stack>
      )}
    </div>
  );
};

export default FunnelStageCard;
